import React, { useEffect, useState, useCallback } from "react";
import Table from 'react-bootstrap/Table';
import '../../projects/scss/task.scss'
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import idx from 'idx'
import { itemFields } from "../../../constants/fields.constant";
import { updateSubTaskAction } from "../../../slice/add-task";
import { useDispatch } from "react-redux";
import _ from "lodash";
import { getInventoryLists, getSuggestedMaterials } from "../../../actions/inventory.action";
import { warningSnackBar } from "../../../actions/common.action";
import { debounceTime, filterItems, filterManufacturer, isInlineItem, INLINE_CATALOG_DISPLAY_NAME, INLINE_ITEM_IDENTIFIER, INLINE_CATEGORY_DISPLAY_NAME, INLINE_ITEM_DEFAULT_UNIT } from "../../../util/common";
import { toDecimal } from "../../../util/decimal-utils";
import { getFormatedUnit } from "../../../util";
import { v4 as uuidv4 } from "uuid";
import ProfitMarginLock from "../../../components/profitMarginLock-dialog";
import { NumberInput } from "../../../components/inputs/new-input";
import { debounce } from "@mui/material/utils";
import ReplaceMaterialModal from "./replaceMaterialModal";


const filter = createFilterOptions();
/**
 * Material Component 
 * 
 * A comprehensive and interactive component for managing material items within a sub-task.
 * Supports advanced filtering, dynamic pricing logic, catalog-based item selection, and 
 * integration with project-level profit margin settings.
 * 
 * @Modified CTBL-275 - Material list UI fix
 * 
 * MAJOR FEATURES:
 * 1. Material Resource Management:
 *    - Displays material items in a structured table format
 *    - Inline editing for unit cost and quantity
 *    - Dynamic calculation of total and sale amount
 *    - Row-level delete option
 *    - Subtotal summary row
 * 
 * 2. Add Material Flow:
 *    - Expandable section with advanced filtering capabilities
 *    - Autocomplete fields for Catalog, Category, Part No., and Item Name
 *    - Unit cost and quantity fields with live total calculation
 *    - Profit margin lock logic integrated for pricing control
 * 
 * 3. State and Redux Integration:
 *    - Uses `updateSubTaskAction` to update materials in sub-task Redux state
 *    - Maintains internal state for filters, items list, dialog state, and unit setup
 *    - Uses debounce for optimized inventory search requests
 * 
 * 4. Smart Profit Margin Handling:
 *    - Allows toggling of sale amount lock per material
 *    - Integrates `ProfitMarginLock` modal for confirmation
 *    - Updates all relevant items on lock confirmation
 * 
 * UI/UX IMPROVEMENTS:
 * 1. Table Design:
 *    - Clean Bootstrap table with responsive layout
 *    - Editable input fields with proper alignment
 *    - Conditional rendering for edit/view mode
 * 
 * 2. Interaction Enhancements:
 *    - Expandable 'Add Material' row with smooth transitions
 *    - Reusable `CustomInput` component ensures consistent behavior
 *    - Real-time feedback on item selection and value updates
 *    - Prevention of duplicate material additions
 * 
 * TECHNICAL ENHANCEMENTS:
 * 1. Performance:
 *    - Debounced API calls for fetching inventory items
 *    - Optimized filtering using utility functions (`filterItems`, `filterManufacturer`)
 *    - Efficient state updates using `lodash.cloneDeep` and immutability principles
 * 
 * 2. Extensibility:
 *    - Designed to accommodate unit list, profit margin config, category filters
 *    - Easily extendable for markup calculations, inline item addition, etc.
 * 
 * 3. Utility Integration:
 *    - Uses `getFormatedUnit` to transform units
 *    - Uses `uuidv4` for generating unique item IDs
 *    - Graceful handling of incomplete or missing data via `idx` utility
 * 
 * @component
 * @param {Object} props
 * @param {Array} props.material - List of materials assigned to the sub-task
 * @param {Object} props.total - Total cost summary for all materials
 * @param {Array} props.categoryList - Available catalog categories
 * @param {Array} props.unitList - Full unit list for UoM dropdowns
 * @param {number} props.profitMargin - Project-level profit margin percentage
 * @param {string} props.profitMarginType - Profit margin type (fixed/percentage)
 * @param {boolean} props.isDisabled - Whether the component is in read-only mode
 * @param {Array} props.subTaskArray - Array of all sub-tasks (from Redux)
 * @param {number} props.subTaskArrayIndex - Index of the current sub-task
 * @returns {JSX.Element} Material list view with edit/add support
 */

export default function Material({ material = [], total = {}, categoryList = [], unitList = [], profitMargin, profitMarginType, isDisabled, showReplaceButton = false, onRefreshTemplateList, ...props }) {

    const dispatch = useDispatch()
    // const permissions = useSelector(state => state.user.userData.permissions) || {}
    // const wCatalogPermission = idx(permissions, _ => _.catalog) === 2 ? true : false
    const [materialState, setMaterialState] = useState({
        categoriesList: [],
        itemsList: [],
        items: [],
        filterValue: {},
        item: itemFields,
        profitMarginDialogState: false,
        lockArrayValue: null,
        confirmLock: false,
        showMaterialLockIcon: true,
    })
    const [expandAddMaterial, setExpandAddMaterial] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [suggestedMaterials, setSuggestedMaterials] = useState([]);
    const [materialToReplace, setMaterialToReplace] = useState(null);

    useEffect(() => {
        initLoad()
    }, [])

    const initLoad = () => {
        debouncedFetchItems();
        const allUnits = (unitList && Array.isArray(unitList) && unitList.length > 0 && unitList[0].units) || [];
        const units = getFormatedUnit(allUnits);
        let { item } = materialState;
        item.unit.items = units;
        setMaterialState(prev => ({
            ...prev,
            item
        }));
    }

    const fetchItems = (filter) => {
        let categoryFilter = (filter && filter.catalog) ? filter.catalog : '';
        let categoryId = (filter && filter.catalogid) ? filter.catalogid : '';
        let manufacturerFilter = (filter && filter.manufacturer) ? filter.manufacturer : '';
        let search = (filter && filter.item) ? filter.item : '';
        let partNoFilter = (filter && filter.partNo) ? filter.partNo : '';
        let params = {};
        params = { ...(categoryId ? { categoryId } : { categoryFilter }), manufacturerFilter, search, partNoFilter }
        if (params.categoryId || params.search || params.partNoFilter) {
            dispatch(getInventoryLists(params, { loader: false })).then((items) => {
                const category = items?.category || [];
                setMaterialState(prev => ({
                    ...prev,
                    itemsList: category
                }));
            });
        } else {
            setMaterialState(prev => ({
                ...prev,
                itemsList: []
            }));
        }
    }

    const debouncedFetchItems = useCallback(
        debounce((filter) => {
            fetchItems(filter)
        }, debounceTime.inventoryList),
        []
    );

    const getCategoriesList = () => {
        return categoryList.map((category) => ({
            key: category.name,
            name: category.name,
            value: category.id,
        }));
    }

    const profitMarginDialogHandler = (inputValue, index) => {
        setMaterialState(prev => ({
            ...prev,
            lockArrayValue: index,
            profitMarginDialogState: !prev.profitMarginDialogState,
        }));
    };

    const itemChangeHandler = (e, index) => {
        let _temp = _.cloneDeep(material)
        switch (e) {
            case "saleAmountLock":
                profitMarginDialogHandler(e, index);
                if (materialState.profitMarginDialogState && materialState.confirmLock) {
                    _temp[index]["saleAmountLock"].value = !_temp[index]["saleAmountLock"].value;
                }
                break;
            default:
                if (e.target.name === "costPerItem" || e.target.name === "qty") {
                    if (e.target.value < 0)
                        break;
                }

                _temp[index][e.target.name].value = e.target.value;

                const is_locked = _temp[index]["saleAmountLock"].value || false;
                let saleAmount = _temp[index]["saleAmount"].value;

                if (e.target.name === "saleAmountLock") {
                    _temp[index]["saleAmountLock"].value = !_temp[index]["saleAmountLock"].value;
                }

                if (e.target.name === "costPerItem" || e.target.name === "qty") {
                    if (saleAmount <= 0 || is_locked) {
                        _temp[index]["saleAmount"].value = _temp[index]["costPerItem"].value
                    }
                }
                saleAmount = _temp[index]["saleAmount"].value;

                _temp[index]["total"].value = toDecimal(_temp[index]["qty"].value || 0).times(toDecimal(saleAmount || 0)).toNumber();
        }

        updateMaterials(_temp);
    };

    const removeItem = (index) => {
        let _temp = _.cloneDeep(material)
        _temp.splice(index, 1)
        updateMaterials(_temp);
    };

    const updateMaterials = (materials) => {
        if (!Array.isArray(materials)) return;
        const { subTaskArray, subTaskArrayIndex } = props;
        let _temp = _.cloneDeep(subTaskArray)
        _temp[subTaskArrayIndex]["material"] = materials;
        dispatch(updateSubTaskAction(_temp));
    };

    const filterItem = (id) => {
        const item = (materialState.itemsList || []).find((item) => item.id === id);
        return item;
    };

    const addItem = (value, category, itemData = null, skip = false) => {
        let selectedMaterials = _.cloneDeep(material);
        const itemExist = selectedMaterials.find((item) => item.id === value && item.categoryId === category);

        if (!itemExist) {
            const { filterValue } = materialState;
            const selectedItem = itemData ? itemData : filterItem(value);
            if (!selectedItem) return;
            if (!category) category = selectedItem.categoryId;
            // if (_.filter(categoryName, { id: category }).map((v) => v.id).length === 0)
            //     this.addCategory(category);

            const item = JSON.parse(JSON.stringify(materialState.item));
            item.name.value = selectedItem.item;
            item.taskId.value = selectedItem.taskId || null;
            item.id = selectedItem.id || uuidv4();
            item.manufacturer = selectedItem.manufacturer;
            item.partNo = selectedItem.partNo || selectedItem.part_no;
            item.categoryId = selectedItem.categoryId;
            const cost = filterValue.costPerItem !== undefined && filterValue.costPerItem !== '' ? parseFloat(filterValue.costPerItem) : parseFloat(selectedItem.cost || 0);
            item.costPerItem.value = cost;
            item.saleAmount.value = ((filterValue.costPerItem !== undefined && filterValue.costPerItem !== '') || filterValue["saleAmountLock"] === false) ?
                (filterValue["saleAmount"] !== undefined ? parseFloat(filterValue["saleAmount"]) : cost) :
                (selectedItem.saleAmountLock ? cost : (parseFloat(selectedItem.saleAmount) || cost));
            item.saleAmountLock.value = filterValue["saleAmountLock"] !== undefined ? filterValue["saleAmountLock"] : selectedItem.saleAmountLock;
            item.unit.value = selectedItem.unit || null;
            item.qty.value = filterValue["qty"] !== undefined && filterValue["qty"] !== '' ? parseFloat(filterValue["qty"]) : (parseFloat(selectedItem.qty) || 1);
            item.total.value = toDecimal(item.qty.value || 0).times(toDecimal(item.saleAmount.value || 0)).toNumber();

            item.catalog_moved = selectedItem.catalog_moved || false;
            item.different_account = selectedItem.different_account || false;
            item.hide_search = selectedItem.hide_search || false;
            item.status = selectedItem.status || 'ACTIVE';
            item.duplicateStatus = selectedItem.duplicateStatus || false;
            selectedMaterials.push(item);

            updateMaterials(selectedMaterials);
            hideAddItem()
        } else {
            if (!skip) {
                dispatch(warningSnackBar("This Item is Already Selected"));
            }
        }
    };

    const hideAddItem = () => {
        setExpandAddMaterial(false);
        setMaterialState(prev => ({
            ...prev,
            filterValue: {},
            itemsList: [],
            totalValue: 0,
            formKey: (prev.formKey || 0) + 1,
        }));
    };

    /**
     * Submits and commits an inline non-catalog material item to the sub-task material list.
     * Validates item name, part number, cost, and quantity against authoritative constraints.
     *
     * @returns {void}
     */
    const handleAddMaterialSubmit = () => {
        const { filterValue } = materialState;
        const itemName = (filterValue?.item || "").trim();

        if (!itemName) {
            dispatch(warningSnackBar("Please enter an Item Name"));
            return;
        }

        const partNo = (filterValue?.partNo || "").trim();
        if (!partNo) {
            dispatch(warningSnackBar("Please enter a Part No"));
            return;
        }

        const cost = parseFloat(filterValue?.costPerItem);
        if (isNaN(cost) || cost <= 0) {
            dispatch(warningSnackBar("Please enter a valid Cost"));
            return;
        }

        const rawQty = filterValue?.qty !== undefined && filterValue?.qty !== '' ? filterValue.qty : 1;
        const qty = parseFloat(rawQty);
        if (isNaN(qty) || qty <= 0) {
            dispatch(warningSnackBar("Please enter a valid Quantity"));
            return;
        }

        if (filterValue?.selectedItemId) {
            addItem(filterValue.selectedItemId, filterValue.catalogid);
            return;
        }

        let selectedMaterials = _.cloneDeep(material);

        const item = JSON.parse(JSON.stringify(materialState.item));
        item.name.value = itemName;
        item.id = uuidv4();
        item.manufacturer = INLINE_CATEGORY_DISPLAY_NAME;
        item.partNo = partNo;
        item.categoryId = INLINE_ITEM_IDENTIFIER;
        item.categoryName = INLINE_CATEGORY_DISPLAY_NAME;
        item.catalog = INLINE_ITEM_IDENTIFIER;
        item.isInlineItem = true;
        item.costPerItem.value = cost;
        item.saleAmount.value = cost;
        item.saleAmountLock.value = true;
        item.unit.value = INLINE_ITEM_DEFAULT_UNIT;
        item.qty.value = qty;
        item.total.value = toDecimal(qty).times(toDecimal(cost)).toNumber();

        item.catalog_moved = false;
        item.different_account = false;
        item.hide_search = false;
        item.status = 'ACTIVE';
        item.duplicateStatus = false;

        selectedMaterials.push(item);
        updateMaterials(selectedMaterials);
        hideAddItem();
    };

    const itemsDropDownOption = () => {
        const { filterValue } = materialState;
        let itemsList = _.cloneDeep(materialState.itemsList)
        let filterOptions = filterItems(itemsList, filterValue);
        if (filterOptions.length === 0) {
            setFilterResult(true, "");
        } else {
            setFilterResult(false, "");
        }
        return filterOptions;
    };
    const setFilterResult = (value, msg) => {
        const { filterValue } = materialState
        let prevValue = idx(filterValue, (_) => _.noresult);
        if (prevValue !== value) {
            filterValue["noresult"] = value;
            filterValue["noresult_msg"] = msg;
            setMaterialState(prev => ({
                ...prev,
                filterValue
            }));
        }
    };

    /**
     * Updates add-material filter values, computes running total, and triggers debounced inventory search.
     *
     * @param {Object} updates - Key-value map of filter fields to update
     * @param {boolean} [shouldFetch=true] - Whether to invoke debounced inventory search
     * @returns {void}
     */
    const updateFilterValues = (updates, shouldFetch = true) => {
        let { showMaterialLockIcon } = materialState;
        setMaterialState(prev => {
            const filterValue = { ...(prev.filterValue || {}), ...updates };
            let totalValue = prev.totalValue;

            if (updates.saleAmount !== undefined && !showMaterialLockIcon) {
                filterValue["saleAmount"] = updates.saleAmount;
                filterValue["saleAmountLock"] = !filterValue["saleAmountLock"];
            }

            if ("costPerItem" in updates || "qty" in updates || "saleAmount" in updates) {
                if (showMaterialLockIcon || filterValue["saleAmountLock"] || filterValue["saleAmount"] === undefined) {
                    filterValue["saleAmount"] = parseFloat(filterValue["costPerItem"] || 0);
                }
                const qtyVal = toDecimal(filterValue["qty"] !== undefined && filterValue["qty"] !== "" ? filterValue["qty"] : 1);
                const costVal = toDecimal(filterValue["saleAmount"] !== undefined && filterValue["saleAmount"] !== "" ? filterValue["saleAmount"] : 0);
                totalValue = qtyVal.times(costVal).toNumber();
            }

            return {
                ...prev,
                filterValue,
                totalValue: parseFloat(totalValue || 0)
            };
        });

        if (shouldFetch) {
            debouncedFetchItems({ ...(materialState.filterValue || {}), ...updates });
        }
    };

    const addFilter = (value, type) => {
        const currentVal = materialState.filterValue ? materialState.filterValue[type] : undefined;
        if (currentVal !== value) {
            const isCalcField = type === "costPerItem" || type === "qty" || type === "saleAmount";
            updateFilterValues({ [type]: value }, !isCalcField);
        }
    };

    const manufacturerDropDown = () => {
        const { filterValue } = materialState;
        let itemsList = _.cloneDeep(materialState.itemsList)
        let filterOptions = filterManufacturer(itemsList, filterValue);
        if (filterOptions.length === 0) {
            setFilterResult(true, "");
        } else {
            setFilterResult(false, "");
        }
        return filterOptions;
    };

    const setConfirmed = () => {
        const { lockArrayValue } = materialState;
        let _selectedItemsList = _.cloneDeep(material);
        _selectedItemsList[lockArrayValue]["saleAmountLock"].value = !_selectedItemsList[lockArrayValue]["saleAmountLock"].value;
        _selectedItemsList.forEach((item, index) => {
            if (item["saleAmountLock"].value) {
                _selectedItemsList[index]["saleAmount"].value = item["costPerItem"].value
                _selectedItemsList[index]["total"].value = toDecimal(item["qty"].value || 0).times(toDecimal(_selectedItemsList[index]["saleAmount"].value || 0)).toNumber();
            }
        });
        setMaterialState(prev => ({
            ...prev,
            profitMarginDialogState: !prev.profitMarginDialogState
        }));
        updateMaterials(_selectedItemsList);
    };

    const partNoDropDown = () => {
        let itemsList = _.cloneDeep(materialState.itemsList)
        let filterOptions = itemsList.map(item => ({
            key: item.partNo || item.part_no,
            value: item.partNo || item.part_no
        }));

        filterOptions = _.uniqBy(filterOptions, 'key');

        return filterOptions;
    };
    const fetchSuggestedMaterials = (material) => {
        const partNo = material?.partNo || '';
        const description = idx(material, _ => _.name.value) || '';
        const itemName = idx(material, _ => _.name.value) || '';

        return dispatch(getSuggestedMaterials({
            item: itemName,
            part_no: partNo,
            item_description: description,
            excludeId: material.id,
        })).then((res) => {
            const suggestedMaterials = res?.suggestedMaterials || [];
            return suggestedMaterials;
        });
    };

    const handleOpenReplaceModal = (material) => {
        setMaterialToReplace(material);
        fetchSuggestedMaterials(material).then((suggestedMaterials) => {
            setSuggestedMaterials(suggestedMaterials);
            setShowReplaceModal(true);
        });
    };



    return (
        <>
            <h2>Material List</h2>
            <div className="table-responsive">
                <Table bordered className="table-create table-material tbl-fixed">
                    <thead>
                        <tr>
                            <th>Catalog</th>
                            <th>Category</th>
                            <th>Part No</th>
                            <th className="item-width">Items Description</th>
                            <th className="cost-width text-right">Unit Cost($)</th>
                            {/* <th>Sale Amount</th> */}
                            <th className="qty-width text-right">Quantity</th>
                            <th className="text-right total-width">Total ($)</th>
                            <th className="text-right action-width">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {material.map((_material, materialIndex) => {
                            if (!_material) return;

                            let category = getCategoriesList().find((item) => item.value === _material.categoryId);
                            return (
                                <tr key={materialIndex}>
                                    <td>{isInlineItem(_material) ? INLINE_CATALOG_DISPLAY_NAME : category?.name || _material.categoryName}</td>
                                    <td>{isInlineItem(_material) ? INLINE_CATEGORY_DISPLAY_NAME : _material.manufacturer}</td>
                                    <td>{_material.partNo}</td>
                                    <td>{idx(_material, _ => _.name.value) || ""}</td>
                                    <td className="input-text-right cost-width">
                                        <NumberInput
                                            className="input-block-sm"
                                            disabled={isDisabled}
                                            decimalPlaces={2}
                                            {..._material.costPerItem}
                                            onChange={(e) => itemChangeHandler(e, materialIndex)}
                                        />
                                    </td>
                                    {/* <td>
                                        <CustomInput
                                            className="input-block-sm"
                                            disabled={true}
                                            placeholder="Sale Amount"
                                            {..._material.saleAmount}
                                        />
                                    </td> */}
                                    <td className="input-text-right qty-width">
                                        <NumberInput
                                            className={'input-block-sm'}
                                            decimalPlaces={2}
                                            {..._material.qty}
                                            disabled={isDisabled}
                                            onChange={(e) => itemChangeHandler(e, materialIndex)}
                                        />
                                    </td>
                                    <td className="text-right total-width">
                                        ${parseFloat(_material.total.value).toFixed(2) || parseFloat(_material.saleAmount.value).toFixed(2) || 0}
                                    </td>
                                    <td className="text-right action-width">
                                        {!isDisabled && showReplaceButton && (_material.status === 'INACTIVE' || _material.hide_search || _material.different_account || _material.duplicateStatus) && (
                                            <button
                                                data-cy="item-replace"
                                                className="btn btn-outline-primary btn-sm ms-2"
                                                onClick={() => {
                                                    setSelectedIndex(materialIndex);
                                                    handleOpenReplaceModal(_material);
                                                }}
                                            >
                                                Replace
                                            </button>
                                        )}
                                        {!isDisabled && <DeleteIcon
                                            className="deleteicon"
                                            onClick={() => removeItem(materialIndex)}
                                        />}
                                    </td>
                                </tr>
                            )
                        })}

                        {expandAddMaterial && (
                            <tr key={materialState.formKey || 0} className="add-material-row">
                                <td>
                                    <div className="input-block-sm autocomplete-block autocomplete-block-ch">
                                        <Autocomplete
                                            disablePortal
                                            freeSolo
                                            id="combo-box-catalog"
                                            onChange={(event, newValue) => {
                                                if (typeof newValue === "string") {
                                                    addFilter(newValue, "catalog");
                                                } else if (newValue && newValue.inputValue) {
                                                    addFilter(newValue.inputValue, "catalog");
                                                } else if (newValue) {
                                                    updateFilterValues({
                                                        catalog: newValue.key,
                                                        catalogid: newValue.value
                                                    }, true);
                                                } else {
                                                    updateFilterValues({
                                                        catalog: "",
                                                        catalogid: ""
                                                    }, true);
                                                }
                                            }}
                                            filterOptions={(options, params) => filter(options, params)}
                                            selectOnFocus
                                            handleHomeEndKeys
                                            clearOnEscape={false}
                                            clearOnBlur={false}
                                            clearText={true}
                                            options={getCategoriesList()}
                                            getOptionLabel={(option) => {
                                                if (typeof option === "string") return option;
                                                return option.key || option.name || "";
                                            }}
                                            onInputChange={(event, newValue, reason) => {
                                                if (reason === "input") {
                                                    addFilter(newValue?.key || newValue, "catalog");
                                                    if (newValue?.value) {
                                                        addFilter(newValue.value, "catalogid");
                                                    }
                                                }
                                            }}
                                            renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select Catalog" />}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="input-block-sm autocomplete-block autocomplete-block-ch">
                                        <Autocomplete
                                            disablePortal
                                            freeSolo
                                            id="combo-box-category"
                                            onChange={(event, newValue) => {
                                                if (typeof newValue === "string") {
                                                    addFilter(newValue, "manufacturer");
                                                } else if (newValue && newValue.inputValue) {
                                                    addFilter(newValue.inputValue, "manufacturer");
                                                } else if (newValue) {
                                                    addFilter(newValue.key || newValue.value, "manufacturer");
                                                } else {
                                                    addFilter("", "manufacturer");
                                                }
                                            }}
                                            filterOptions={(options, params) => filter(options, params)}
                                            selectOnFocus
                                            handleHomeEndKeys
                                            clearOnEscape={false}
                                            clearOnBlur={false}
                                            clearText={true}
                                            options={manufacturerDropDown()}
                                            getOptionLabel={(option) => {
                                                if (typeof option === "string") return option;
                                                return option.key ? option.key : "";
                                            }}
                                            onInputChange={(event, newValue, reason) => {
                                                if (reason === "input") {
                                                    addFilter(newValue, "manufacturer");
                                                }
                                            }}
                                            renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select Category" />}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="input-block-sm autocomplete-block autocomplete-block-ch">
                                        <Autocomplete
                                            disablePortal
                                            freeSolo
                                            id="combo-box-partno"
                                            onChange={(event, newValue) => {
                                                if (typeof newValue === "string") {
                                                    addFilter(newValue, "partNo");
                                                } else if (newValue && newValue.inputValue) {
                                                    addFilter(newValue.inputValue, "partNo");
                                                } else if (newValue) {
                                                    addFilter(newValue.key || newValue.value, "partNo");
                                                } else {
                                                    addFilter("", "partNo");
                                                }
                                            }}
                                            filterOptions={(options, params) => filter(options, params)}
                                            selectOnFocus
                                            handleHomeEndKeys
                                            clearOnEscape={false}
                                            clearOnBlur={false}
                                            clearText={true}
                                            options={partNoDropDown({})}
                                            getOptionLabel={(option) => {
                                                if (typeof option === "string") return option;
                                                return option.key ? option.key : "";
                                            }}
                                            onInputChange={(event, newValue, reason) => {
                                                if (reason === "input") {
                                                    addFilter(newValue, "partNo");
                                                }
                                            }}
                                            renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select Part No" />}
                                        />
                                    </div>
                                </td>
                                <td className="item-width">
                                    <div className="input-block-sm autocomplete-block autocomplete-block-ch">
                                        <Autocomplete
                                            disablePortal
                                            freeSolo
                                            id="combo-box-items"
                                            onChange={(event, newValue) => {
                                                if (typeof newValue === "string") {
                                                    addFilter(newValue, "item");
                                                } else if (newValue && newValue.inputValue) {
                                                    addFilter(newValue.inputValue, "item");
                                                } else if (newValue && (newValue.value || newValue.id)) {
                                                    addItem(newValue.value || newValue.id, newValue.categoryId || materialState.filterValue?.catalogid);
                                                } else {
                                                    addFilter("", "item");
                                                }
                                            }}
                                            selectOnFocus
                                            handleHomeEndKeys
                                            clearOnEscape={false}
                                            clearOnBlur={false}
                                            clearText={true}
                                            onInputChange={(event, newValue, reason) => {
                                                if (reason === "input") {
                                                    addFilter(newValue, "item");
                                                }
                                            }}
                                            options={itemsDropDownOption()}
                                            getOptionLabel={(option) => {
                                                if (typeof option === "string") return option;
                                                if (option.inputValue) return option.inputValue;
                                                return option.key || option.item || "";
                                            }}
                                            filterOptions={(options, params) => {
                                                const filtered = filter(options, params);
                                                if (params.inputValue !== "") {
                                                    filtered.push({
                                                        inputValue: params.inputValue,
                                                        key: `Add "${params.inputValue}"`,
                                                    });
                                                }
                                                return filtered;
                                            }}
                                            renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select Item Description" />}
                                        />
                                    </div>
                                </td>
                                <td className="input-text-right cost-width">
                                    <NumberInput
                                        className="input-block-sm"
                                        name="costPerItem"
                                        decimalPlaces={2}
                                        value={materialState.filterValue?.costPerItem ?? ""}
                                        onChange={(e) => {
                                            if (e.target.value >= 0) addFilter(e.target.value, "costPerItem");
                                        }}
                                    />
                                </td>
                                <td className="input-text-right qty-width">
                                    <NumberInput
                                        className="input-block-sm"
                                        name="qty"
                                        decimalPlaces={2}
                                        value={materialState.filterValue?.qty !== undefined ? materialState.filterValue.qty : 1}
                                        onChange={(e) => {
                                            if (e.target.value >= 0) addFilter(e.target.value, "qty");
                                        }}
                                    />
                                </td>
                                <td className="text-right total-width">
                                    ${(materialState.totalValue || 0).toFixed(2)}
                                </td>
                                <td className="text-right action-width">
                                    <Tooltip
                                        title="Add as Inline Item"
                                        placement="top"
                                        arrow
                                        followCursor={true}
                                        PopperProps={{ style: { zIndex: 9999 } }}
                                    >
                                        <AddIcon
                                            className="add-icon me-1 cursor-pointer"
                                            onClick={handleAddMaterialSubmit}
                                            data-cy="add-material-btn"
                                        />
                                    </Tooltip>
                                    <Tooltip
                                        title="Cancel"
                                        placement="top"
                                        arrow
                                        followCursor={true}
                                        PopperProps={{ style: { zIndex: 9999 } }}
                                    >
                                        <DeleteIcon
                                            className="deleteicon cursor-pointer"
                                            onClick={hideAddItem}
                                        />
                                    </Tooltip>
                                </td>
                            </tr>
                        )}

                        <tr>
                            <td colSpan={4}>
                                {!isDisabled && <span
                                    className="add-material"
                                    onClick={() => {
                                        if (expandAddMaterial) {
                                            hideAddItem();
                                        } else {
                                            setMaterialState(prev => ({
                                                ...prev,
                                                filterValue: {},
                                                itemsList: [],
                                                totalValue: 0,
                                                formKey: (prev.formKey || 0) + 1,
                                            }));
                                            setExpandAddMaterial(true);
                                        }
                                    }}
                                    aria-controls="example-collapse-texts"
                                    aria-expanded={expandAddMaterial}
                                >
                                    <AddIcon /> Add Material
                                </span>}
                            </td>
                            <td className="cost-width"></td>
                            <td className="text-right table-text-sm qty-width">Sub Total</td>
                            <td className="text-right table-text-sm total-width">
                                ${parseFloat(idx(total, (_) => _.material.sale) || 0).toFixed(2)}
                            </td>
                            <td className="action-width"></td>
                        </tr>
                    </tbody>
                </Table>
            </div>

            

            <ReplaceMaterialModal
                show={showReplaceModal}
                onClose={() => setShowReplaceModal(false)}
                material={materialToReplace}
                suggestedMaterials={suggestedMaterials}
                onSelect={(replacementMaterial) => {
                    const updated = _.cloneDeep(material);
                    const old = updated[selectedIndex];
                    const cost = parseFloat(replacementMaterial.cost || 0);
                    const qty = parseFloat(old.qty?.value || 1);
                    const saleAmount = parseFloat(replacementMaterial.sale_amount || cost);
                    const unit = replacementMaterial.unit || old.unit?.value || '';
                    const saleAmountLock = !!replacementMaterial.sale_amount_lock;
                    const transformed = {
                        ...old,
                        id: replacementMaterial.id,
                        name: { ...old.name, value: replacementMaterial.item || replacementMaterial.name || '' },
                        partNo: replacementMaterial.part_no || replacementMaterial.partNo || '',
                        categoryId:
                            replacementMaterial.category_id ||
                            (replacementMaterial.category && replacementMaterial.category.id) ||
                            old.categoryId || '',
                        categoryName: replacementMaterial.category?.name || '',
                        manufacturer: replacementMaterial.manufacturer || '',
                        status: replacementMaterial.status || 'ACTIVE',
                        catalog_moved: false,
                        hide_search: false,
                        different_account: false,
                        duplicateStatus: false,
                        costPerItem: { ...old.costPerItem, value: cost },
                        qty: { ...old.qty, value: qty },
                        saleAmount: { ...old.saleAmount, value: saleAmount },
                        saleAmountLock: { ...old.saleAmountLock, value: saleAmountLock },
                        unit: { ...old.unit, value: unit },
                        total: { ...old.total, value: cost * qty },
                    };
                    updated[selectedIndex] = transformed;
                    updateMaterials(updated);
                    setShowReplaceModal(false);

                    // Refresh template list after successful replace
                    if (onRefreshTemplateList && typeof onRefreshTemplateList === 'function') {
                        onRefreshTemplateList();
                    }
                }}
            />
            <ProfitMarginLock
                open={materialState.profitMarginDialogState}
                lockIconChange={() => setConfirmed()}
                dialogClose={profitMarginDialogHandler}
                moduleName="Task"
                modalHeading={"Sale Amount Lock changes"}
                text={"sale Amount"}
                message={'Are you sure you want to change the Project profit margin? - Any items using default profit margins will also be updated to this value'}
            />
        </>
    );

}
